import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setMessage } from "./message";

import userService from "../services/user.service";

// Generic thunk for fetching category videos
export const getCategoryVideos = createAsyncThunk(
  "dashboardCarrouselVideos/getCategoryVideos",
  async (queryParams, thunkAPI) => {
    try {
      const response = await userService.getAllVideos(queryParams);
      thunkAPI.dispatch(setMessage(response.data));
      return { 
        typeId: queryParams.typeId, 
        videos: response.data.details 
      };
    } catch (error) {
      thunkAPI.dispatch(setMessage(error.response));
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getFavoriteVideos = createAsyncThunk(
  "dashboardCarrouselVideos/getFavoriteVideos",
  async (queryParams, thunkAPI) => {
    try {
      const response = await userService.getFavoriteVideos(queryParams);
      thunkAPI.dispatch(setMessage(response.data));
      return response.data.details;
    } catch (error) {
      thunkAPI.dispatch(setMessage(error.response));
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getLastPlayedVideos = createAsyncThunk(
  "dashboardCarrouselVideos/getLastPlayedVideos",
  async (queryParams, thunkAPI) => {
    try {
      const response = await userService.getLastPlayedVideos(queryParams);
      thunkAPI.dispatch(setMessage(response.data));
      return response.data.details;
    } catch (error) {
      thunkAPI.dispatch(setMessage(error.response));
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getLatestVideos = createAsyncThunk(
  "dashboardCarrouselVideos/latestVideos",
  async (queryParams, thunkAPI) => {
    try {
      const response = await userService.getLatestVideos(queryParams);
      thunkAPI.dispatch(setMessage(response.data));
      return response.data.details;
    } catch (err) {
      thunkAPI.dispatch(setMessage(err.response));
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const setPage = createAsyncThunk(
  "dashboardCarrouselVideos/setPage",
  async (data, thunkAPI) => {
    return { page: data.page, typeId: data.typeId, type: data.type };
  }
);

export const refreshLikesDislikes = createAsyncThunk(
  "dashboardCarrouselVideos/refreshLikesDislikes",
  async (data, thunkAPI) => {
    return data;
  }
);

export const setFavoritesPage = createAsyncThunk(
  "dashboardCarrouselVideos/setFavoritesPage",
  async (data, thunkAPI) => {
    return data;
  }
);

const initialState = {
  favorite: { videos: [], page: 1 },
  latestVideos: { videos: [], page: 1 },
  lastPlayedVideos: { videos: [], page: 1 },
  isFetching: false,
  isFetched: false,
  categories: {}, // Dynamic categories keyed by typeId
  showAllVideos: localStorage.getItem("show_all_videos") === "true",
};

const dashboardCarrouselVideosSlice = createSlice({
  name: "dashboardCarrouselVideos",
  initialState,
  reducers: {
    toggleShowAllVideos: (state) => {
      state.showAllVideos = !state.showAllVideos;
      localStorage.setItem("show_all_videos", state.showAllVideos.toString());
      // Reset categories so videos are re-fetched with the new filter
      state.categories = {};
      state.favorite = { videos: [], page: 1 };
      state.latestVideos = { videos: [], page: 1 };
      state.lastPlayedVideos = { videos: [], page: 1 };
    },
    setShowAllVideos: (state, action) => {
      state.showAllVideos = action.payload;
      localStorage.setItem("show_all_videos", state.showAllVideos.toString());
      state.categories = {};
      state.favorite = { videos: [], page: 1 };
      state.latestVideos = { videos: [], page: 1 };
      state.lastPlayedVideos = { videos: [], page: 1 };
    },
  },
  extraReducers: {
    // Generic category videos handler
    [getCategoryVideos.fulfilled]: (state, action) => {
      const { typeId, videos } = action.payload;
      
      // Initialize category if it doesn't exist
      if (!state.categories[typeId]) {
        state.categories[typeId] = { 
          videos: [], 
          page: 1, 
          videoType: "", 
          videoTypeId: typeId 
        };
      }
      
      state.isFetching = false;
      state.isFetched = true;
      
      // Store videoType from first video if available
      if (videos.length > 0) {
        state.categories[typeId].videoType = videos[0].videoType;
        state.categories[typeId].videoTypeId = videos[0].videoTypeId;
      }
      
      // Append or replace videos based on page number
      state.categories[typeId].videos =
        state.categories[typeId].page === 1
          ? [...videos]
          : [...state.categories[typeId].videos, ...videos];
      
      state.categories[typeId].page = state.categories[typeId].page + 1;
    },
    [getCategoryVideos.rejected]: (state, action) => {
      state.isFetching = false;
      state.isFetched = false;
    },
    [getLastPlayedVideos.fulfilled]: (state, action) => {
      state.isFetching = false;
      state.isFetched = true;
      state.lastPlayedVideos.videos =
        state.lastPlayedVideos.page === 1
          ? [...action.payload]
          : [...state.lastPlayedVideos.videos, ...action.payload];
      state.lastPlayedVideos.page = state.lastPlayedVideos.page + 1;
    },
    [getLastPlayedVideos.rejected]: (state, action) => {
      state.isFetching = false;
      state.isFetched = false;
    },
    [setFavoritesPage.fulfilled]: (state, action) => {
      state.favorite.page = action.payload;
    },
    [setFavoritesPage.rejected]: (state, action) => {},
    [getFavoriteVideos.fulfilled]: (state, action) => {
      state.isFetching = false;
      state.isFetched = true;
      state.favorite.videos =
        state.favorite.page === 1
          ? [...action.payload]
          : [...state.favorite.videos, ...action.payload];

      if (action.payload.length > 0) {
        state.favorite.page = state.favorite.page + 1;
      }
    },
    [getFavoriteVideos.rejected]: (state, action) => {
      state.isFetching = false;
      state.isFetched = false;
    },
    [getLatestVideos.fulfilled]: (state, action) => {
      state.isFetching = false;
      state.isFetched = true;
      state.latestVideos.videos =
        state.latestVideos.page === 1
          ? [...action.payload]
          : [...state.latestVideos.videos, ...action.payload];
      state.latestVideos.page = state.latestVideos.page + 1;
    },
    [getLatestVideos.rejected]: (state, action) => {
      state.isFetching = false;
      state.isFetched = false;
    },
    [refreshLikesDislikes.fulfilled]: (state, action) => {
      // Update all dynamic categories
      Object.keys(state.categories).forEach((typeId) => {
        state.categories[typeId].videos = state.categories[typeId].videos.map((video) => {
          if (
            video.id == action.payload.videoId ||
            video.id == action.payload.sessionVideoId
          ) {
            video.isFavourite = action.payload.isFavourite;
          }
          return video;
        });
      });

      // Update special arrays
      state.latestVideos.videos = state.latestVideos.videos.map((video) => {
        if (
          video.id == action.payload.videoId ||
          video.id == action.payload.sessionVideoId
        )
          video.isFavourite = action.payload.isFavourite;
        return video;
      });

      state.lastPlayedVideos.videos = state.lastPlayedVideos.videos.map(
        (video) => {
          if (
            video.id == action.payload.videoId ||
            video.id == action.payload.sessionVideoId
          )
            video.isFavourite = action.payload.isFavourite;
          return video;
        }
      );

      state.favorite.videos = state.favorite.videos.map((video) => {
        if (
          video.id == action.payload.videoId ||
          video.id == action.payload.sessionVideoId
        )
          video.isFavourite = action.payload.isFavourite;
        return video;
      });
    },
    [setPage.fulfilled]: (state, action) => {
      // Handle special types
      if (action.payload.type === "favorites") {
        state.favorite.page = action.payload.page;
      } else if (action.payload.type === "last-played") {
        state.lastPlayedVideos.page = action.payload.page;
      } else if (action.payload.type === "latest-videos") {
        state.latestVideos.page = action.payload.page;
      } else if (action.payload.typeId && state.categories[action.payload.typeId]) {
        // Handle dynamic categories by typeId
        state.categories[action.payload.typeId].page = action.payload.page;
      }
    },
    [setPage.rejected]: (state, action) => {},
  },
});

const { reducer, actions } = dashboardCarrouselVideosSlice;
export const { toggleShowAllVideos, setShowAllVideos } = actions;
export default reducer;
